import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { authApi } from '@/api/auth'
import { X } from 'lucide-react'
import { PasswordInput } from '@/components/common/PasswordInput'
import { PASSWORD_HINT, passwordSchema } from '@/lib/passwordPolicy'

const schema = z
  .object({
    current_password: z.string().min(1, 'Введите текущий пароль'),
    new_password: passwordSchema,
    confirm: z.string(),
  })
  .refine((d) => d.new_password === d.confirm, {
    message: 'Пароли не совпадают',
    path: ['confirm'],
  })

type FormData = z.infer<typeof schema>

interface Props {
  onClose: () => void
}

const inputCls = 'block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500'
const labelCls = 'block text-sm font-medium text-gray-700 mb-1'
const errorCls = 'mt-1 text-xs text-rose-600'

export default function ChangePasswordModal({ onClose }: Props) {
  const overlayRef = useRef<HTMLDivElement>(null)
  const [serverError, setServerError] = useState('')
  const [success, setSuccess] = useState(false)

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', h)
    return () => document.removeEventListener('keydown', h)
  }, [onClose])

  async function onSubmit(data: FormData) {
    setServerError('')
    try {
      await authApi.changePassword(data.current_password, data.new_password)
      setSuccess(true)
    } catch (err: any) {
      const status = err?.response?.status
      if (status === 401) setServerError('Текущий пароль неверен')
      else if (status === 422) setServerError(err?.response?.data?.detail ?? 'Пароль не соответствует требованиям')
      else setServerError('Ошибка сервера. Попробуйте позже.')
    }
  }

  return (
    <div
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onClose() }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
    >
      <div className="w-full max-w-sm mx-4 rounded-xl bg-white shadow-xl border border-gray-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">Смена пароля</h2>
          <button onClick={onClose} className="rounded-lg p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {success ? (
          <div className="p-6 text-center space-y-4">
            <div className="text-emerald-500 text-4xl">✓</div>
            <p className="text-sm font-medium text-gray-900">Пароль успешно изменён</p>
            <button onClick={onClose} className="w-full rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 transition-colors">
              Закрыть
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="p-6 space-y-4">
            <div>
              <label className={labelCls}>Текущий пароль</label>
              <PasswordInput autoComplete="current-password" {...register('current_password')} className={inputCls} />
              {errors.current_password && <p className={errorCls}>{errors.current_password.message}</p>}
            </div>
            <div>
              <label className={labelCls}>Новый пароль</label>
              <PasswordInput autoComplete="new-password" {...register('new_password')} className={inputCls} />
              {errors.new_password && <p className={errorCls}>{errors.new_password.message}</p>}
              <p className="mt-1 text-xs text-gray-400">{PASSWORD_HINT}</p>
            </div>
            <div>
              <label className={labelCls}>Подтверждение</label>
              <PasswordInput autoComplete="new-password" {...register('confirm')} className={inputCls} />
              {errors.confirm && <p className={errorCls}>{errors.confirm.message}</p>}
            </div>
            {serverError && (
              <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700">{serverError}</div>
            )}
            <div className="flex gap-3 pt-1">
              <button type="submit" disabled={isSubmitting}
                className="flex-1 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50 transition-colors">
                {isSubmitting ? 'Сохранение...' : 'Сохранить'}
              </button>
              <button type="button" onClick={onClose}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                Отмена
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
